import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { brand } from '../portalTheme';
import { EmptyState, ErrorState, LoadingState, PageTitle, StatusChip, useAsync } from '../PortalUi';
import {
  CustomerProduct,
  fetchInterestRequests,
  fetchProducts,
  portalErrorMessage,
  submitProductInterest,
} from '../../../../services/request/CustomerPortal';
import { formatDate, inr } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

const CATEGORIES = ['ALL', 'FINANCIAL_PRODUCTS', 'SAAS', 'CONSULTING_SERVICES', 'CLOTHES'];

export default function PortalProducts() {
  const { t, term } = useTranslation();
  const [category, setCategory] = useState('ALL');
  const products = useAsync(() => fetchProducts(category === 'ALL' ? undefined : category), [category]);
  const requests = useAsync(fetchInterestRequests, []);
  const [selected, setSelected] = useState<CustomerProduct | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const productNames = new Map((products.data ?? []).map(product => [product.id, product.name]));

  const submit = async () => {
    if (!selected) return;
    setSending(true);
    setFormError(null);
    try {
      await submitProductInterest(selected.id, message);
      setNotice(t('portal.products.thanks', { name: selected.name }));
      setSelected(null);
      setMessage('');
      requests.reload();
    } catch (error) {
      setFormError(portalErrorMessage(error, t('portal.products.sendError')));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <PageTitle kicker={t('portal.products.kicker')} title={t('portal.products.title')} subtitle={t('portal.products.subtitle')} />
      {notice ? (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {CATEGORIES.map(value => (
          <Chip
            key={value}
            label={value === 'ALL' ? t('portal.products.all') : term('category', value)}
            onClick={() => setCategory(value)}
            sx={
              category === value
                ? { bgcolor: brand.navy, color: brand.ivory, '&:hover': { bgcolor: brand.charcoal } }
                : { bgcolor: brand.white, border: `1px solid ${brand.border}` }
            }
          />
        ))}
      </Box>
      {products.error ? <ErrorState message={products.error} onRetry={products.reload} /> : null}
      {products.loading && !products.data ? <LoadingState label={t('portal.products.loading')} /> : null}
      {products.data && products.data.length === 0 ? <EmptyState title={t('portal.products.empty')} /> : null}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
        {(products.data ?? []).map(product => (
          <Paper key={product.id} sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {product.imageUrl ? (
              <Box component="img" src={product.imageUrl} alt={product.name} sx={{ width: '100%', height: 170, objectFit: 'cover' }} />
            ) : null}
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              {product.category ? (
                <Typography variant="overline" sx={{ color: brand.gold }}>
                  {term('category', product.category)}
                </Typography>
              ) : null}
              <Typography variant="h6">{product.name}</Typography>
              {product.description ? <Typography color="text.secondary">{product.description}</Typography> : null}
              {product.features ? (
                <Typography variant="body2" color="text.secondary">
                  {product.features}
                </Typography>
              ) : null}
              {typeof product.price === 'number' ? <Typography sx={{ fontWeight: 700 }}>{inr(product.price)}</Typography> : null}
              <Box sx={{ mt: 'auto', pt: 1 }}>
                {product.subscriptionLink ? (
                  <Button variant="contained" color="secondary" fullWidth href={product.subscriptionLink} target="_blank" rel="noopener noreferrer">
                    {t('portal.products.open')}
                  </Button>
                ) : (
                  <Button variant="contained" fullWidth onClick={() => setSelected(product)}>
                    {t('portal.products.registerInterest')}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        {t('portal.products.requestsTitle')}
      </Typography>
      {requests.error ? <ErrorState message={requests.error} onRetry={requests.reload} /> : null}
      {requests.data && requests.data.length === 0 ? <EmptyState title={t('portal.products.requestsEmpty')} /> : null}
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {(requests.data ?? []).map(request => (
          <Paper key={request.id} sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>
                {(request.productId && productNames.get(request.productId)) || t('portal.common.product')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {request.message || t('portal.products.noMessage')} · {formatDate(request.createdDate) ?? t('portal.common.dateMissing')}
              </Typography>
            </Box>
            <StatusChip status={request.status} />
          </Paper>
        ))}
      </Box>

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('portal.products.dialogTitle', { name: selected?.name ?? '' })}</DialogTitle>
        <DialogContent>
          {formError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          ) : null}
          <TextField
            label={t('portal.products.message')}
            multiline
            minRows={3}
            fullWidth
            value={message}
            onChange={event => setMessage(event.target.value)}
            inputProps={{ maxLength: 500 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setSelected(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submit} disabled={sending}>
            {sending ? t('common.sending') : t('common.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
